import { inject, injectable } from "inversify";
import {
  ICentrosDeCustoParams,
  IClienteResponse,
  IDREListModelos,
  IEPessoasFinancas,
  IFinancasFeatureGateway,
  IGetClientesParams,
  IFornecedoresConfiaveisFinancas,
  IObterCentroDeCustoCCResponse,
  IObterCentroDeCustoPorIdResponse,
  IUserProfilePayload,
  TExportarDadosDoDREPayload
} from "../ports/FinancasFeatureGateway";
import {
  IDefaultHorizonsResponse,
  IFinancasDefaultResponse,
  IPlanosDeContasHttpResponse
} from "@/core/services/financas/gateways/types";
import { DITypes, IDefaultHttpFinancasResponse } from "@/core/@types/types";
import { type HttpClient } from "@/core/services/protocols/types";
import {
  FinancasDevHttpDi,
  type IFinancasDevHttp
} from "@/core/services/FinancasDevHttp";

@injectable()
export default class FinancasFeatureGatewayAdapter implements IFinancasFeatureGateway {
  @inject(DITypes.FINANCAS_HTTP_CLIENT)
  private httpClient?: HttpClient;

  @inject(DITypes.INDICADORES_HTTP_CLIENT)
  private httpClientIndicadores!: HttpClient;

  @inject(FinancasDevHttpDi)
  private financasDevHttp!: IFinancasDevHttp;

  @inject(DITypes.FINANCAS_API_HTTP_CLIENT)
  private httpClientFinancasApi!: HttpClient;

  async getPlanosDeContas(
    tipo: boolean
  ): Promise<IPlanosDeContasHttpResponse | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/PlanoDeContaAPI/Listar/?Tipo=${tipo}`
      );

      return {
        Data: response.Data,
        Total: response.Total || 0
      };
    }

    const response = await this.httpClientFinancasApi?.get<
      IPlanosDeContasHttpResponse | undefined
    >({
      url: `/v1/PlanoDeContaAPI/Listar/?Tipo=${tipo}`
    });

    if (!response?.body) return;

    return response?.body;
  }

  async obterCentroDeCustoPorId(
    id: string
  ): Promise<
    | IDefaultHorizonsResponse<IObterCentroDeCustoPorIdResponse | undefined>
    | undefined
  > {
    const response = await this.httpClientIndicadores?.get<
      IDefaultHorizonsResponse<IObterCentroDeCustoPorIdResponse | undefined>
    >({
      url: "/financas/centrodecusto/v1/obter-centros-custo-by-customer-id",
      queryParams: {
        objectid: id
      }
    });

    return response?.body;
  }

  async obterCentroDeCustoCC(): Promise<
    | IFinancasDefaultResponse<IObterCentroDeCustoCCResponse[] | undefined>
    | undefined
  > {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/v1/CentroDeCustoAPI/ListarTodosCC/"
      );

      return {
        Data: response.Data,
        Total: response.Total || 0
      };
    }

    const response = await this.httpClientFinancasApi?.get<
      IFinancasDefaultResponse<IObterCentroDeCustoCCResponse[] | undefined>
    >({
      url: "/v1/CentroDeCustoAPI/ListarTodosCC/"
    });

    return response?.body;
  }

  async obterCentrosDeCustos(
    params?: ICentrosDeCustoParams
  ): Promise<
    | IFinancasDefaultResponse<IObterCentroDeCustoCCResponse[] | undefined>
    | undefined
  > {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/v1/CentroDeCustoAPI/ListarTodosCC/",
        params
      );

      return {
        Data: response.Data,
        Total: response.Total || 0
      };
    }

    const response = await this.httpClientFinancasApi?.get<
      IFinancasDefaultResponse<IObterCentroDeCustoCCResponse[] | undefined>
    >({
      url: "/v1/CentroDeCustoAPI/ListarTodosCC/",
      queryParams: { ...params }
    });

    return response?.body;
  }

  async getPessoas(): Promise<IEPessoasFinancas | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/PessoaAPI/ListarQuebradoPorInscricaoEManterPessoasSemInscricao?definicao=null"
      );

      return {
        Data: response.Data,
        Total: response.Total || 0
      };
    }

    const response = await this.httpClientFinancasApi?.get<
      IEPessoasFinancas | undefined
    >({
      url: "/v1/PessoaAPI/ListarQuebradoPorInscricaoEManterPessoasSemInscricao?definicao=null"
    });

    return response?.body;
  }

  async getClientes(
    params: IGetClientesParams
  ): Promise<IFinancasDefaultResponse<IClienteResponse[] | undefined>> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get("/api/ClienteAPI/List", {
        ...params
      });

      return {
        Data: response.Data,
        Total: response.Total || 0
      };
    }
    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<IClienteResponse[] | undefined>
    >({
      url: "/ClienteAPI/List",
      queryParams: {
        ...params
      }
    });

    return (
      response?.body ?? {
        Data: [],
        Total: 0
      }
    );
  }

  async obterEmpresaPorConfiguracao(
    id: string
  ): Promise<IDefaultHttpFinancasResponse<string> | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/EmpresaAPI/ObterEmpresaPorConfiguracao/?serviceId=${id}`
      );

      return {
        Result: response.Result,
        Ok: response.Ok,
        Error: response.Error
      };
    }
    const response = await this.httpClient?.get<
      IDefaultHttpFinancasResponse<string> | undefined
    >({
      url: `/EmpresaAPI/ObterEmpresaPorConfiguracao/?serviceId=${id}`
    });

    return response?.body;
  }

  async getFornecedoresConfiaveis(): Promise<
    IDefaultHttpFinancasResponse<IFornecedoresConfiaveisFinancas[]> | undefined
  > {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/PessoaAPI/ListarMin/?Definicao=false`
      );

      return {
        Data: response.Data,
        Total: response.Total || 0
      };
    }
    const response = await this.httpClient?.get<
      | IDefaultHttpFinancasResponse<IFornecedoresConfiaveisFinancas[]>
      | undefined
    >({
      url: `/PessoaAPI/ListarMin/?Definicao=false`
    });

    return response?.body;
  }

  async exportarDadosDoDRE(
    params: TExportarDadosDoDREPayload
  ): Promise<IDefaultHttpFinancasResponse<boolean> | undefined> {
    try {
      if (import.meta.env.DEV) {
        const response = await this.financasDevHttp.post(
          "/api/v1/DREAPI/ExportarDRE/",
          params
        );

        return {
          Ok: response.Ok || false,
          Error: response?.Error || undefined,
          Result: response?.Result || false
        } as IDefaultHttpFinancasResponse<boolean>;
      }
      const response = await this.httpClientFinancasApi?.post<
        IDefaultHttpFinancasResponse<boolean> | undefined
      >({
        url: "/v1/DREAPI/ExportarDRE/",
        body: params
      });

      return (
        response?.body ??
        ({
          Ok: false,
          Error: undefined,
          Result: false
        } as IDefaultHttpFinancasResponse<boolean>)
      );
    } catch (error) {
      console.error(error);

      return {
        Ok: false,
        Error: "Erro ao exportar o relatório do DRE",
        Result: false
      } as IDefaultHttpFinancasResponse<boolean>;
    }
  }

  async updateUserProfile(
    params: IUserProfilePayload
  ): Promise<IDefaultHttpFinancasResponse<boolean> | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.post(
        "/api/UsuarioAPI/UpdateCurrentUserProfile/",
        params
      );

      return {
        Ok: response.Ok || false,
        Error: response?.Error || undefined,
        Result: response?.Result || false
      } as IDefaultHttpFinancasResponse<boolean>;
    }

    const response = await this.httpClient?.post<
      IDefaultHttpFinancasResponse<boolean> | undefined
    >({
      url: "/UsuarioAPI/UpdateCurrentUserProfile/",
      body: params
    });

    return response?.body;
  }

  async atualizarTelefoneUsuario(
    telefone: string,
    isWhatsapp: boolean
  ): Promise<IDefaultHttpFinancasResponse<boolean> | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.post(
        "/api/UsuarioAPI/AtualizarTelefoneUsuario/",
        { Telefone: telefone, IsWhatsapp: isWhatsapp }
      );

      return {
        Ok: response.Ok || false,
        Error: response?.Error || undefined,
        Result: response?.Result || false
      } as IDefaultHttpFinancasResponse<boolean>;
    }

    const response = await this.httpClient?.post<
      IDefaultHttpFinancasResponse<boolean> | undefined
    >({
      url: "/UsuarioAPI/AtualizarTelefoneUsuario/",
      body: { Telefone: telefone, IsWhatsapp: isWhatsapp }
    });

    return response?.body;
  }

  async getModelosDREList(): Promise<
    IDefaultHttpFinancasResponse<IDREListModelos[] | undefined> | undefined
  > {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/DREAPI/List?sort=&group=&filter=`
      );

      return {
        Ok: response.Ok || false,
        Error: response?.Error || undefined,
        Result: response?.Result || [],
        Data: response.Data,
        Total: response.Total || 0
      };
    }

    const response = await this.httpClientFinancasApi?.get<
      IDefaultHttpFinancasResponse<IDREListModelos[] | undefined>
    >({
      url: "/v1/DREAPI/List?sort=&group=&filter="
    });

    return response?.body;
  }
}
